import moment from 'moment-timezone'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { customNumberFormat, currencyFormatter } from '../../functions/general'
import useAccessToken from '../../hooks/AccessToken'
import { useIntl } from 'react-intl'
import useUser from '../../hooks/User'
import { createColumnHelper } from '@tanstack/react-table'
import DataTableAccountReceivable from '../AccountReceivable/DataTableAccountReceivable'
import useSWR from 'swr'
import Swal from 'sweetalert2'
import axios from 'axios'
import { KTSVG } from '../../_metronic/helpers'
import { useMerchantStore } from '../../stores/MerchantStore'
import { usePipelineStore } from '../../stores/PipelineStore'
import { CustomField, SettingMultiMerchants, SettingMultiPipelines } from '../../interfaces/Settings'
import { useSettingsStore } from '../../stores/SettingsStore'
import { useReceivableStore } from '../../stores/Receivable'
import { DetailReportTermin } from '../../interfaces/ReportTermin'
import { handleChangeValue as helperHandleChangeValue, handleChangeKeyName } from '../../helpers/DetailViewHelpers'
import MultiAttachmentModal from '../ReportTermin/MultiAttachmentModal'

const API_URL = process.env.REACT_APP_API_URL

interface DetailAccountReceivableProps {
    receivableId: string
}

const DetailReportInvoice = ({ receivableId }: DetailAccountReceivableProps) => {
    const token = useAccessToken()
    const intl = useIntl()
    const user = useUser()
    const { multiMerchants } = useMerchantStore()
    const { multiPipelines } = usePipelineStore()
    const [customFieldTermin, setCustomFieldTermin] = useState<CustomField>({})
    const [customFieldMerchant, setCustomFieldMerchant] = useState<CustomField>({})
    const [customFieldPipeline, setCustomFieldPipeline] = useState<CustomField>({})
    const { settings } = useSettingsStore()
    const { selectTemplateIdPipeline } = useReceivableStore()
    const [templateIdMerchant, setTemplateIdMerchant] = useState<string>('')
    const [resultFinalFilteredData, setResultFinalFilteredData] = useState<any[]>([])
    const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false)
    const [attachmentModalData, setAttachmentModalData] = useState<{ title: string; url: string | string[] }>({ title: '', url: '' })
    const modalRef = useRef<HTMLDivElement | null>(null);   

    useEffect(() => {
        if (settings.termins_custom_fields) {
            setCustomFieldTermin(JSON.parse(settings.termins_custom_fields))
        }
    }, [settings])

    useEffect(() => {
        if (multiPipelines.length > 0) {
            const getCustomFieldByLeadTemplateId = multiPipelines.reduce((acc: CustomField, pipeline: SettingMultiPipelines) => {
                if (pipeline.id === selectTemplateIdPipeline) {
                    acc = { ...acc, ...pipeline.customFields }
                }
                return acc
            }, {})
            setCustomFieldPipeline(getCustomFieldByLeadTemplateId)
        }
    }, [multiPipelines, selectTemplateIdPipeline])

    useEffect(() => {
        if (multiMerchants.length > 0) {
            const getCustomFieldByLeadTemplateId = multiMerchants.reduce((acc: CustomField, pipeline: SettingMultiMerchants) => {
                if (pipeline.id === templateIdMerchant) {
                    acc = { ...acc, ...pipeline.customFields }
                }
                return acc
            }, {})
            setCustomFieldMerchant(getCustomFieldByLeadTemplateId)
        }
    }, [multiMerchants, templateIdMerchant])

    const handleCheckIsTypeAttachment = (attachmentUrl: string | string[] | File | File[], attachmentKey?: string, title?: string) => {
        const isFileArray = (value: any[]): value is File[] => {
            return value.every((item): item is File => item instanceof File);
        };

        const isFile = attachmentUrl instanceof File || (Array.isArray(attachmentUrl) && isFileArray(attachmentUrl));

        if (isFile) {
            console.log('File attachment:', attachmentUrl);
        } else {
            setAttachmentModalData({
                title: title || attachmentKey || 'Attachment',
                url: attachmentUrl as string | string[]
            });
            setShowAttachmentModal(true);
        }
    };

    const handleCloseAttachmentModal = () => {
        setShowAttachmentModal(false);
        setAttachmentModalData({ title: '', url: '' });
    };

    const fetcher = (token: string) => async (url: string) => {
        try {
            const response = await axios.get(url, {
                headers: {
                    'x-auth-token': token
                }
            })
            return response.data.data
        } catch (error: any) {
            console.error(error)
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error'
            })
        }
    }

    const { data: dataDetailAccountReceivable, isLoading } = useSWR<DetailReportTermin>(`${API_URL}receivable-reports/detail?leadId=${receivableId}`, fetcher(token))

    useEffect(() => {
        let newData: any[] = []

        if (Object.keys(customFieldPipeline).length > 0 && dataDetailAccountReceivable?.customFieldPipe) {
            const newValues = Object.keys(customFieldPipeline).map((key) => {
                return [
                    customFieldPipeline[key].name,
                    dataDetailAccountReceivable?.customFieldPipe[key] || ''
                ]
            })
            newData.push(...newValues)
        }

        if (Object.keys(customFieldMerchant).length > 0 && dataDetailAccountReceivable?.customFieldMerchant) {
            const newValues = Object.keys(customFieldMerchant).map((key) => {
                return [
                    customFieldMerchant[key].name,
                    dataDetailAccountReceivable?.customFieldMerchant[key] || ''
                ]
            })
            newData.push(...newValues)
        }

        if (Object.keys(customFieldTermin).length > 0 && dataDetailAccountReceivable?.customFieldTermin) {
            const newValues = Object.keys(customFieldTermin).map((key) => {
                return [
                    customFieldTermin[key].name,
                    dataDetailAccountReceivable?.customFieldTermin[key] || ''
                ]
            })
            newData.push(...newValues)
        }

        setResultFinalFilteredData(newData)
    }, [customFieldPipeline, customFieldMerchant, customFieldTermin, dataDetailAccountReceivable])

    useEffect(() => {
        if (dataDetailAccountReceivable) {
            setTemplateIdMerchant(dataDetailAccountReceivable.templateIdMerchant)
        }
    }, [dataDetailAccountReceivable])

    const columnHelper = createColumnHelper<any>()
    const columns = [
        columnHelper.display({
            id: 'no',
            header: 'No',
            cell: (info) => info.row.index + 1,
            meta: {
                rowSpan: 3,
                styleHeader: {
                    alignContent: "center",
                    textAlign: "center"
                },
                styleBody: {
                    textAlign: "center"
                }
            },
        }),
        columnHelper.accessor('tanggalJatuhTempo', {
            header: 'Tanggal Jatuh Tempo',
            cell: (info) => info.getValue() ? moment(info.getValue()).format('DD-MMM-YYYY') : '-',
            meta: {
                rowSpan: 3,
                styleHeader: {
                    alignContent: "center",
                    textAlign: "center"
                }
            },
        }),
        columnHelper.accessor('keterangan', {
            header: 'Keterangan',
            meta: {
                rowSpan: 3,
                styleHeader: {
                    alignContent: "center",
                    textAlign: "center"
                }
            },
        }),
        columnHelper.group({
            header: 'Pembayaran',
            columns: [
                columnHelper.accessor('dpp', {
                    header: 'DPP',
                    cell: (info) => currencyFormatter(info.getValue(), user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency, { showPrefix: true }),
                    meta: {
                        styleHeader: {
                            alignContent: "center",
                            textAlign: "center"
                        },
                        styleBody: {
                            textAlign: "right"
                        }
                    },
                }),
                columnHelper.accessor('ppn', {
                    header: 'PPN',
                    cell: (info) => currencyFormatter(info.getValue(), user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency, { showPrefix: true }),
                    meta: {
                        styleHeader: {
                            alignContent: "center",
                            textAlign: "center"
                        },
                        styleBody: {
                            textAlign: "right"
                        }
                    },
                }),
                columnHelper.accessor('total', {
                    header: 'Jumlah',
                    cell: (info) => currencyFormatter(info.getValue(), user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency, { showPrefix: true }),
                    meta: {
                        styleHeader: {
                            alignContent: "center",
                            textAlign: "center"
                        },
                        styleBody: {
                            textAlign: "right"
                        }
                    },
                })
            ],
            meta: {
                styleHeader: {
                    alignContent: "center",
                    textAlign: "center"
                }
            },
        }),
        columnHelper.accessor('tanggalPembayaran', {
            header: 'Tanggal Pembayaran',
            cell: (info) => info.getValue() ? moment(info.getValue()).format('DD-MMM-YYYY') : '-',
            meta: {
                rowSpan: 3,
                styleHeader: {
                    alignContent: "center",
                    textAlign: "center"
                }
            },
        }),
    ];

    const terminArray = Object.keys((dataDetailAccountReceivable as any)?.dataTermin || {}).map((key, index) => ({
        no: index + 1,
        ...(dataDetailAccountReceivable as any)?.dataTermin[key],
    }));

    const listKeyForHide = ['dataTermin', 'totalTermin', 'customFieldMerchant', 'customFieldPipe', 'customFieldTermin', 'templateIdMerchant']

    const filteredData = Object.entries(dataDetailAccountReceivable || {}).filter(
        ([key]) => !listKeyForHide.includes(key)
    )

    const resultFilteredData = [...filteredData, ...resultFinalFilteredData]

    const handleChangeValueLocal = (key: string, value: any, type?: string) => {
        return helperHandleChangeValue({
            key,
            value,
            type,
            user,
            intl,
            onAttachmentClick: handleCheckIsTypeAttachment
        });
    }

    const handleCloseModal = () => {
        modalRef.current?.click();
    }

    return (
        <div className='modal fade' tabIndex={-1} id={`detail-receivable-account-${receivableId}`} ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    {isLoading && (
                        <div className="loading-overlay">
                            <div className="loader"></div>
                        </div>
                    )}
                    <div className="modal-header">
                        <h5 className="modal-title w-100">{intl.formatMessage({ id: "FORM.MENU.TERM_DETAILS" })} {intl.formatMessage({ id: 'MENU.ACCOUNT_RECEIVABLE' })}</h5>
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            onClick={handleCloseModal}
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <div className="modal-body">
                        <div style={{ padding: "0 2.25rem" }}>
                            {resultFilteredData.map(([key, value]) => (
                                <div className="row pb-2" key={key}>
                                    <div className="col-3">
                                        {handleChangeKeyName(key)}
                                    </div>
                                    <div className="col-9" style={{ fontWeight: "500", color: "black" }}>
                                        {handleChangeValueLocal(key, value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ paddingTop: "2.25rem" }}>
                            <DataTableAccountReceivable data={terminArray || []} tableColumns={columns} />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Multi Attachment Modal */}
            <MultiAttachmentModal
                title={attachmentModalData.title}
                url={attachmentModalData.url}
                isVisible={showAttachmentModal}
                onClose={handleCloseAttachmentModal}
            />
        </div>
    );
}

export default DetailReportInvoice